import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Heart, MessageCircle, ShoppingBag, Truck, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, inr, waLink } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import ProductCard from "@/components/ProductCard";
import { imgUrl } from "@/components/ImageUploader";

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [img, setImg] = useState(0);
  const [waNum, setWaNum] = useState("919100042473");
  const [showSize, setShowSize] = useState(false);
  const { add } = useCart();
  const { toggle, has } = useWishlist();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setData(r.data);
      setSize(r.data.product.sizes?.[0] || "");
      setColor(r.data.product.colors?.[0] || "");
      setImg(0);
    });
    api.get("/whatsapp-info").then((r) => setWaNum(r.data.number));
  }, [slug]);

  if (!data) return <div className="max-w-5xl mx-auto p-8 animate-pulse">Loading…</div>;
  const p = data.product;

  const handleAdd = () => {
    if (p.sizes.length && !size) return toast.error("Select a size");
    add({ product_id: p.id, name: p.name, image: p.images[0], size, color, price: p.price, quantity: 1 });
    toast.success("Added to cart");
  };

  const waText = `Hi Nisa Libas, I'd love to order:\n\n${p.name}\nSize: ${size || "—"} · Color: ${color || "—"}\nPrice: ${inr(p.price)}\n\nLink: ${window.location.href}`;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10">
      <div className="grid md:grid-cols-2 gap-8 md:gap-12">
        {/* gallery */}
        <div>
          <div className="aspect-[3/4] bg-[#FCE4EC] overflow-hidden">
            <img src={imgUrl(p.images[img])} alt={p.name} className="w-full h-full object-cover" data-testid="pdp-main-image" />
          </div>
          <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
            {p.images.map((im, i) => (
              <button key={i} onClick={() => setImg(i)} className={`w-16 h-20 md:w-20 md:h-24 shrink-0 overflow-hidden ${img === i ? "ring-2 ring-[#D4AF37]" : "opacity-60"}`} data-testid={`thumb-${i}`}>
                <img src={imgUrl(im)} className="w-full h-full object-cover" alt="" />
              </button>
            ))}
          </div>
        </div>

        {/* details */}
        <div>
          <div className="eyebrow mb-3">{p.subcategory} · {p.fabric}</div>
          <h1 className="font-serif text-3xl md:text-4xl text-[#0B1F3A] leading-tight" data-testid="pdp-name">{p.name}</h1>
          <div className="flex items-baseline gap-3 mt-4">
            <span className="text-2xl font-semibold text-[#D4AF37]" data-testid="pdp-price">{inr(p.price)}</span>
            {p.compare_at_price && p.compare_at_price > p.price && (
              <>
                <span className="text-lg text-[#1E293B]/40 line-through" data-testid="pdp-mrp">MRP {inr(p.compare_at_price)}</span>
                <span className="text-xs bg-[#D4AF37]/20 text-[#0B1F3A] px-2 py-0.5" data-testid="pdp-discount">{Math.round((1 - p.price / p.compare_at_price) * 100)}% off · Save {inr(p.compare_at_price - p.price)}</span>
              </>
            )}
          </div>
          <p className="text-sm text-[#1E293B]/80 leading-relaxed mt-4">{p.description}</p>

          {p.colors?.length > 0 && (
            <div className="mt-6">
              <div className="eyebrow mb-2">Color · <span className="text-[#0B1F3A] normal-case tracking-normal">{color}</span></div>
              <div className="flex flex-wrap gap-2">
                {p.colors.map((c) => (
                  <button key={c} onClick={() => setColor(c)}
                    className={`text-xs px-4 py-2.5 min-h-[44px] border ${color === c ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#0B1F3A]" : "border-[#0B1F3A]/20 text-[#1E293B]/70"}`}
                    data-testid={`pdp-color-${c}`}>{c}</button>
                ))}
              </div>
            </div>
          )}

          {p.sizes?.length > 0 && (
            <div className="mt-6">
              <div className="flex justify-between items-baseline mb-2">
                <div className="eyebrow">Size</div>
                <button onClick={() => setShowSize(true)} className="text-xs underline text-[#0B1F3A]" data-testid="size-guide-btn">Size guide</button>
              </div>
              <div className="flex flex-wrap gap-2">
                {p.sizes.map((s) => (
                  <button key={s} onClick={() => setSize(s)}
                    className={`min-w-[52px] min-h-[44px] px-4 py-2.5 text-sm border ${size === s ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#0B1F3A] font-semibold" : "border-[#0B1F3A]/20"}`}
                    data-testid={`pdp-size-${s}`}>{s}</button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 md:mt-8 hidden md:grid grid-cols-1 gap-3">
            <button onClick={handleAdd} className="bg-[#0B1F3A] text-[#FFF9E6] py-4 tracking-widest uppercase text-sm hover:bg-[#163056] flex items-center justify-center gap-2" data-testid="pdp-add-cart">
              <ShoppingBag size={16} /> Add to Cart
            </button>
            <a href={waLink(waNum, waText)} target="_blank" rel="noreferrer" className="bg-[#25D366] text-white py-4 tracking-widest uppercase text-sm hover:bg-[#1ebf58] flex items-center justify-center gap-2" data-testid="pdp-whatsapp">
              <MessageCircle size={16} /> Order on WhatsApp
            </a>
            <button onClick={() => toggle(p.id)} className="border py-3 text-sm tracking-widest uppercase flex items-center justify-center gap-2" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid="pdp-wishlist">
              <Heart size={14} fill={has(p.id) ? "#D4AF37" : "none"} color={has(p.id) ? "#D4AF37" : "#0B1F3A"} /> {has(p.id) ? "Saved" : "Save"}
            </button>
          </div>
          {/* Mobile inline wishlist button - primary actions handled by sticky bottom bar */}
          <button onClick={() => toggle(p.id)} className="md:hidden mt-6 w-full border py-3 text-sm tracking-widest uppercase flex items-center justify-center gap-2" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid="pdp-wishlist-mobile">
            <Heart size={14} fill={has(p.id) ? "#D4AF37" : "none"} color={has(p.id) ? "#D4AF37" : "#0B1F3A"} /> {has(p.id) ? "Saved to Wishlist" : "Save to Wishlist"}
          </button>

          <div className="grid grid-cols-2 gap-3 mt-8 text-xs">
            <div className="flex items-center gap-2 text-[#1E293B]/70"><Truck size={14} className="text-[#D4AF37]" /> Free India shipping</div>
            <div className="flex items-center gap-2 text-[#1E293B]/70"><RefreshCw size={14} className="text-[#D4AF37]" /> 7-day returns</div>
          </div>

          {/* Story Behind the Stitch */}
          <div className="mt-10 bg-white p-6 gold-border" data-testid="story-block">
            <div className="eyebrow mb-3 flex items-center gap-2"><Sparkles size={14} /> Story behind the stitch</div>
            <p className="text-sm text-[#1E293B]/80 leading-relaxed italic">{p.story}</p>
          </div>
        </div>
      </div>

      {/* Related */}
      {data.related?.length > 0 && (
        <section className="mt-16 md:mt-24">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-serif text-2xl md:text-3xl text-[#0B1F3A]">Pairs well with</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {data.related.map((r) => <ProductCard key={r.id} p={r} />)}
          </div>
        </section>
      )}

      {/* Sticky mobile bottom CTA bar */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#FCE4EC]/95 backdrop-blur-md border-t p-3 flex gap-2" style={{ borderColor: "rgba(114,47,55,0.2)" }} data-testid="mobile-cta-bar">
        <a href={waLink(waNum, waText)} target="_blank" rel="noreferrer" className="w-12 h-12 shrink-0 bg-[#25D366] text-white flex items-center justify-center rounded-sm" data-testid="mobile-wa-btn" aria-label="Order on WhatsApp">
          <MessageCircle size={20} />
        </a>
        <button onClick={handleAdd} className="flex-1 bg-[#0B1F3A] text-[#FFF9E6] font-semibold tracking-widest uppercase text-xs flex items-center justify-center gap-2" data-testid="mobile-add-cart">
          <ShoppingBag size={16} /> Add to Cart · {inr(p.price)}
        </button>
      </div>
      <div className="md:hidden h-20" aria-hidden />
      {showSize && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowSize(false)}>
          <div className="bg-[#FCE4EC] max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-xl text-[#0B1F3A] mb-4">Size guide</h3>
            <table className="w-full text-sm border-collapse">
              <thead><tr className="border-b border-[#D4AF37]/30"><th className="text-left py-2">Size</th><th>Bust</th><th>Waist</th><th>Hip</th></tr></thead>
              <tbody className="text-[#1E293B]/80">
                {[["XS","32","26","36"],["S","34","28","38"],["M","36","30","40"],["L","38","32","42"],["XL","40","34","44"]].map((r) => (
                  <tr key={r[0]} className="border-b border-[#D4AF37]/10"><td className="py-2 font-semibold">{r[0]}</td><td className="text-center">{r[1]}"</td><td className="text-center">{r[2]}"</td><td className="text-center">{r[3]}"</td></tr>
                ))}
              </tbody>
            </table>
            <button onClick={() => setShowSize(false)} className="mt-4 bg-[#0B1F3A] text-[#FFF9E6] px-6 py-2 text-sm">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
